import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Logo } from './Logo';
import { Phone, Mail, MapPin, Cable, Wifi, Home } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_FOOTER = {
  description: "Connecting skilled technicians with leading telecommunications providers. Subcontracted installation services for cable TV, internet, telephone, and fiber optics.",
  phone: "1-800-MIGHTY-LINK",
  email: "careers@mightylink.com",
  location: "Nationwide Coverage",
  quick_links_title: "Quick Links",
  services_title: "We Install",
  services: [
    "Fiber Optic",
    "Internet & Cable TV",
    "Telephone Systems",
    "Residential & Commercial"
  ],
  copyright: "Mighty Link Field Services"
};

const serviceIcons = [
  <Cable size={14} className="text-violet-500" />,
  <Wifi size={14} className="text-cyan-500" />,
  <Phone size={14} className="text-violet-400" />,
  <Home size={14} className="text-cyan-400" />
];

export const Footer = () => {
  const [footer, setFooter] = useState(DEFAULT_FOOTER);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API_URL}/site-content`);
        if (response.data?.footer) {
          setFooter({ ...DEFAULT_FOOTER, ...response.data.footer });
        }
      } catch (error) {
        // Use default content on error
      }
    };
    fetchContent();
  }, []);

  return (
    <footer className="bg-[#050507] text-white relative overflow-hidden border-t border-slate-800">
      {/* Decorative gradient line */}
      <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-violet-500 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
          {/* Brand */}
          <div className="sm:col-span-2">
            <div className="mb-6">
              <Logo variant="light" />
            </div>
            <p className="text-slate-500 mb-6 max-w-sm text-sm sm:text-base">
              {footer.description}
            </p>
            <div className="flex flex-col gap-3 text-slate-500 text-sm">
              <a href={`tel:${footer.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-3 hover:text-violet-400 transition-colors">
                <Phone size={18} className="text-violet-500" />
                <span>{footer.phone}</span>
              </a>
              <a href={`mailto:${footer.email}`} className="flex items-center gap-3 hover:text-violet-400 transition-colors">
                <Mail size={18} className="text-violet-500" />
                <span>{footer.email}</span>
              </a>
              <div className="flex items-center gap-3">
                <MapPin size={18} className="text-violet-500" />
                <span>{footer.location}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-base sm:text-lg mb-4 text-slate-300" style={{ fontFamily: 'Syne, sans-serif' }}>
              {footer.quick_links_title}
            </h4>
            <ul className="space-y-3 text-slate-500 text-sm">
              <li>
                <Link to="/" className="hover:text-violet-400 transition-colors">Home</Link>
              </li>
              <li>
                <a href="/#services" className="hover:text-violet-400 transition-colors">Services</a>
              </li>
              <li>
                <a href="/#about" className="hover:text-violet-400 transition-colors">About Us</a>
              </li>
              <li>
                <Link to="/register" className="hover:text-violet-400 transition-colors">Join Our Team</Link>
              </li>
            </ul>
          </div>

          {/* Installation Services */}
          <div>
            <h4 className="font-semibold text-base sm:text-lg mb-4 text-slate-300" style={{ fontFamily: 'Syne, sans-serif' }}>
              {footer.services_title}
            </h4>
            <ul className="space-y-3 text-slate-500 text-sm">
              {footer.services.map((service, index) => (
                <li key={`${service}-${index}`} className="flex items-center gap-2">
                  {serviceIcons[index % serviceIcons.length]}
                  <span>{service}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-600 text-xs sm:text-sm text-center sm:text-left">
            © {new Date().getFullYear()} {footer.copyright}. All rights reserved.
          </p>
          <div className="flex gap-4 sm:gap-6 text-slate-600 text-xs sm:text-sm">
            <a href="#" className="hover:text-violet-400 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-violet-400 transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
